import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useCallback, useContext, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logError, logWarning } from '@/utils/errorHandlerUtil';

import AdminSectionHeader from '../AdminSectionHeader';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useTestTelemetry from '@/hooks/Admin/useTestTelemetry';
import useCache from '@/hooks/useCache';

const TELEMETRY_COOLDOWN_SECONDS = 30;
const SITE_TOOLS_TABS = ['Telemetry', 'Cache'] as const;
type SiteToolsTab = (typeof SITE_TOOLS_TABS)[number];

class IntentionalTelemetryTestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IntentionalTelemetryTestError';
  }
}

function createIntentionalTelemetryTestError(): Error {
  return new IntentionalTelemetryTestError('Intentional telemetry test error.');
}

function throwIntentionalTelemetryTestError(): never {
  throw createIntentionalTelemetryTestError();
}

const ManageSiteTools: React.FC = () => {
  const { testTelemetry } = useTestTelemetry();
  const { bustCache } = useCache();
  const [activeTab, setActiveTab] = React.useState<SiteToolsTab>(SITE_TOOLS_TABS[0]);
  const [isSendingTelemetry, setIsSendingTelemetry] = React.useState(false);
  const [isBustingCache, setIsBustingCache] = React.useState(false);
  const [bustCacheError, setBustCacheError] = React.useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [cooldownRemainingSeconds, setCooldownRemainingSeconds] = React.useState(0);
  const { setAlert } = useContext(SnackbarContext);

  useEffect(() => {
    if (cooldownRemainingSeconds <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setCooldownRemainingSeconds((previousValue) => {
        if (previousValue <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return previousValue - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [cooldownRemainingSeconds]);

  const handleTestErrorTelemetry = useCallback(async () => {
    if (isSendingTelemetry || cooldownRemainingSeconds > 0) {
      return;
    }

    setIsConfirmOpen(false);
    setIsSendingTelemetry(true);

    const occurredAtUtc = new Date().toISOString();
    const warningMessage = `Telemetry test warning triggered from admin Site Tools tab at ${occurredAtUtc}.`;
    const errorMessage = `Telemetry test error triggered from admin Site Tools tab at ${occurredAtUtc}.`;

    logWarning(warningMessage);
    try {
      throwIntentionalTelemetryTestError();
    } catch (error) {
      logError(errorMessage, error, {
        telemetryTestSource: 'admin.site-tools',
      });
    }

    try {
      await testTelemetry();
    } catch {
      setAlert({
        message: 'Telemetry data has been sent - check the logs.',
        severity: 'success',
      });
    } finally {
      setCooldownRemainingSeconds(TELEMETRY_COOLDOWN_SECONDS);
      setIsSendingTelemetry(false);
    }
  }, [cooldownRemainingSeconds, isSendingTelemetry, setAlert, testTelemetry]);

  const isButtonDisabled = isSendingTelemetry || cooldownRemainingSeconds > 0;
  const buttonText =
    cooldownRemainingSeconds > 0
      ? `Test Error Telemetry (${cooldownRemainingSeconds}s)`
      : 'Test Error Telemetry';

  const handleBustCache = useCallback(async () => {
    const proceed = window.confirm('This will clear server and browser cache values. Continue?');
    if (!proceed) return;

    setIsBustingCache(true);
    setBustCacheError(null);

    try {
      await bustCache();
      setAlert({
        message: 'Cache cleared successfully.',
        severity: 'success',
      });
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clear cache.';
      setBustCacheError(message);
    } finally {
      setIsBustingCache(false);
    }
  }, [bustCache, setAlert]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <AdminSectionHeader title='Site Tools' />

      <Tabs
        value={activeTab}
        onChange={(_, value: SiteToolsTab) => setActiveTab(value)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          backgroundColor: 'background.default',
        }}
      >
        {SITE_TOOLS_TABS.map((tab) => (
          <Tab key={tab} label={tab} value={tab} />
        ))}
      </Tabs>

      <Box sx={{ p: 2, flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {activeTab === 'Telemetry' && (
          <>
            <Typography sx={{ mb: 2 }}>
              Use these tools for site diagnostics and telemetry verification.
            </Typography>

            <Button
              key='test-error-telemetry'
              variant='secondary'
              onClick={() => {
                setIsConfirmOpen(true);
              }}
              disabled={isButtonDisabled}
            >
              {buttonText}
            </Button>
          </>
        )}

        {activeTab === 'Cache' && (
          <>
            <Typography sx={{ mb: 1.5 }}>
              Clear server cache and browser cache, then reload the app.
            </Typography>

            <Button
              variant='secondary'
              startIcon={<RefreshIcon fontSize='small' />}
              onClick={() => {
                void handleBustCache();
              }}
              disabled={isBustingCache}
              sx={{ mb: 1.5 }}
            >
              {isBustingCache ? 'Clearing Cache...' : 'Bust Cache'}
            </Button>

            {bustCacheError != null && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {bustCacheError}
              </Alert>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={isConfirmOpen}
        onClose={isSendingTelemetry ? undefined : () => setIsConfirmOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogContent>
          <Typography variant='body1'>
            This will send a test exception to Application Insights — continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            variant='secondary'
            onClick={() => setIsConfirmOpen(false)}
            disabled={isSendingTelemetry}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              void handleTestErrorTelemetry();
            }}
            disabled={isButtonDisabled}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSiteTools;
